/**
 * Cache handling mirrors `useApproveAssetStep`: the response already carries the updated
 * progress, so it seeds the read cache directly, and the file detail refetches because
 * overriding the last step completes the chain.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { track } from '@/shared/analytics';

import { overrideAssetStep } from './approval-actions.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { overrideAssetStep };

interface UseOverrideAssetStepOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after overriding. */
  folderId?: string;
}

interface OverrideAssetStepVariables {
  assetId: string;
  stepId: string;
}

export function useOverrideAssetStep(options: UseOverrideAssetStepOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ assetId, stepId }: OverrideAssetStepVariables) =>
      overrideAssetStep(projectId, assetId, stepId),
    onSuccess: (data, { assetId }) => {
      queryClient.setQueryData(ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId), data);
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, assetId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_step_overridden', { project_id: projectId });
    },
  });
}
